import { AbsoluteFill, Audio, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { BEATS, chainAt, PERIOD, RINGS, TAU, ringWeight, smoothstep } from "./score";

const CX = 540;
const CY = 850;
const trail = (seconds: number) => {
  const from = Math.max(0, seconds - PERIOD);
  const count = 210;
  return Array.from({ length: count + 1 }, (_, i) => {
    const t = from + (seconds - from) * i / count;
    const tip = chainAt(t)[RINGS.length - 1];
    return `${i === 0 ? "M" : "L"}${tip.x.toFixed(2)},${tip.y.toFixed(2)}`;
  }).join(" ");
};

export const FourierRhythmMechanical: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const time = frame / fps;
  const chain = chainAt(time);
  const active = BEATS.filter((beat) => time >= beat.time && time - beat.time <= 0.24);
  const fullness = RINGS.filter((ring) => time >= ring.enter).length;
  const exit = 1 - smoothstep((time - 11.5) / 0.5);

  return (
    <AbsoluteFill style={{
      backgroundColor: "#07101D",
      color: "#F3F8FF",
      fontFamily: "Arial, Helvetica, sans-serif",
    }}>
      <Audio src={staticFile("rhythm/mechanical-a.wav")} />
      <div style={{ position: "absolute", left: 88, top: 202, fontSize: 24, letterSpacing: 5.5, color: "#A1B1C4" }}>
        LOOP NIBBLE
      </div>
      <div style={{ position: "absolute", top: 318, width: "100%", textAlign: "center", fontSize: 53, fontWeight: 750, letterSpacing: -1.5 }}>
        ROTATION MAKES <span style={{ color: "#25D7C2" }}>RHYTHM</span>
      </div>
      <div style={{ position: "absolute", top: 397, width: "100%", textAlign: "center", fontSize: 25, color: "#A1B1C4", letterSpacing: 3 }}>
        {fullness} / 4 SOUND LAYERS
      </div>
      <svg viewBox="0 0 1080 1920" width="1080" height="1920" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <radialGradient id="rhythm-glow"><stop offset="0" stopColor="#12334A" stopOpacity=".48"/><stop offset="1" stopColor="#12334A" stopOpacity="0"/></radialGradient>
        </defs>
        <circle cx={CX} cy={CY} r="495" fill="url(#rhythm-glow)" />
        <g transform={`translate(${CX},${CY})`}>
          <path d={trail(time)} stroke="#54DEED" strokeWidth="5" fill="none" strokeOpacity=".83" strokeLinecap="round" strokeLinejoin="round" />
          {RINGS.map((ring, index) => {
            const p = chain[index];
            const weight = ringWeight(ring, time);
            const recent = active.filter((b) => b.ringIndex === index);
            const sparkle = recent.reduce((max, b) => Math.max(max, 1 - (time - b.time) / 0.24), 0);
            if (weight <= 0) return null;
            return (
              <g key={ring.frequency} opacity={Math.max(0.18, weight)}>
                <circle cx={p.cx} cy={p.cy} r={p.r} fill="none" stroke={ring.color} strokeWidth={2.8 + sparkle * 3} strokeOpacity={0.43 + sparkle * 0.5} />
                {Array.from({ length: ring.markers }, (_, marker) => {
                  const a = TAU * marker / ring.markers;
                  const xx = p.cx + p.r * Math.cos(a);
                  const yy = p.cy + p.r * Math.sin(a);
                  return <circle key={marker} cx={xx} cy={yy} r={4} fill={ring.color} opacity=".68" />;
                })}
                <line x1={p.cx} y1={p.cy} x2={p.x} y2={p.y} stroke={ring.color} strokeWidth={3.7} />
                <circle cx={p.x} cy={p.y} r={6 + sparkle * 6} fill={ring.color} />
              </g>
            );
          })}
          {active.map((beat) => {
            const point = chainAt(beat.time)[beat.ringIndex];
            const life = 1 - (time - beat.time) / 0.24;
            return <g key={beat.id}>
              <circle cx={point.x} cy={point.y} r={9 + (1 - life) * 38} stroke={RINGS[beat.ringIndex].color} strokeWidth={2.8} strokeOpacity={0.85 * life} fill="none" />
              <circle cx={point.x} cy={point.y} r={6 + (1 - life) * 12} fill={RINGS[beat.ringIndex].color} opacity={life}/>
            </g>;
          })}
          <circle cx={chain[3].x} cy={chain[3].y} r="8" fill="#FFF9FC" />
        </g>
        <line x1="130" x2="950" y1="1370" y2="1370" stroke="#3D4D63" strokeWidth="2"/>
        <text x="540" y="1446" fontSize="24" textAnchor="middle" fill="#A1B1C4" letterSpacing="5">SOUND SOURCES</text>
        {RINGS.map((ring, index) => {
          const y = 1516 + index * 84;
          const weight = ringWeight(ring, time);
          const last = active.filter((b) => b.ringIndex === index);
          const pulse = last.reduce((max, b) => Math.max(max, 1 - (time - b.time) / 0.24), 0);
          return <g key={ring.frequency} opacity={weight <= 0 ? 0.14 : 0.4 + 0.6 * Math.max(weight, pulse)}>
            <circle cx="206" cy={y - 7} r={11 + 10 * pulse} fill={ring.color} />
            <text x="255" y={y} fill="#F3F8FF" fontSize="29" fontWeight="600">{ring.timbre.toUpperCase()}</text>
            <rect x="610" y={y - 26} width="310" height="20" rx="10" fill="#172C3E"/>
            <rect x="610" y={y - 26} width={Math.max(0, 310 * pulse)} height="20" rx="10" fill={ring.color}/>
            <text x="933" y={y - 7} textAnchor="end" fill={ring.color} fontSize="20">{ring.frequency}x</text>
          </g>;
        })}
        <text x="540" y="1850" fill="#69879D" textAnchor="middle" fontSize="23" letterSpacing="2">EVERY FLASH IS A REAL BEAT</text>
      </svg>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `linear-gradient(to bottom, transparent 85%, rgba(7,16,29,${(1-exit)*0.65}) 100%)` }}/>
    </AbsoluteFill>
  );
};
